/**
 * Workspace API client
 */

import { fetchWithAuth } from './authClient';
import { AUTH_API_URL } from './config';

export interface Workspace {
  id: string;
  name: string;
  workspace_type: 'personal' | 'dm' | 'group';
  storage_url?: string;
  profile_type_policy: string[];
  verification_requirements: string[];
  created_at: string;
  updated_at: string;
  /** Present when listing workspaces — includes invited memberships */
  membership_status?: 'active' | 'invited';
  membership_role?: string;
  /** Number of active members in the workspace (returned by list endpoint) */
  member_count?: number;
}

export interface WorkspaceMember {
  workspace_id: string;
  profile_id: string;
  profile_name: string;
  role: string;
  status: string;
  created_at: string;
  updated_at: string;
}

// In-flight promise coalescing and short-lived caching to prevent parallel request storms
let inFlightListWorkspaces: Promise<Workspace[]> | null = null;
let cachedWorkspaces: { data: Workspace[]; timestamp: number } | null = null;
const WORKSPACE_CACHE_TTL_MS = 5000; // 5 seconds memory cache

const inFlightGetWorkspace = new Map<string, Promise<Workspace>>();
const cachedWorkspaceItems = new Map<string, { data: Workspace; timestamp: number }>();

export function invalidateWorkspaceCache(): void {
  inFlightListWorkspaces = null;
  cachedWorkspaces = null;
  inFlightGetWorkspace.clear();
  cachedWorkspaceItems.clear();
}

if (typeof window !== 'undefined') {
  window.addEventListener('yada:profile:changed', invalidateWorkspaceCache);
  window.addEventListener('yada:profiles:updated', invalidateWorkspaceCache);
  window.addEventListener('yada:account:updated', invalidateWorkspaceCache);
}

/**
 * Create a new workspace
 */
export async function createWorkspace(data: {
  name: string;
  storage_url?: string;
  workspace_type?: 'personal' | 'dm' | 'group';
  profile_type_policy?: string[];
  verification_requirements?: string[];
}): Promise<Workspace> {
  invalidateWorkspaceCache();
  const response = await fetchWithAuth(`${AUTH_API_URL}/api/workspaces`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to create workspace');
  }

  const result = await response.json();
  invalidateWorkspaceCache();
  return result.workspace;
}

/**
 * List workspaces for current profile (deduplicated & short-cached)
 */
export async function listWorkspaces(forceRefresh = false): Promise<Workspace[]> {
  const now = Date.now();
  if (!forceRefresh && cachedWorkspaces && now - cachedWorkspaces.timestamp < WORKSPACE_CACHE_TTL_MS) {
    return cachedWorkspaces.data;
  }

  if (inFlightListWorkspaces) {
    return inFlightListWorkspaces;
  }

  inFlightListWorkspaces = (async () => {
    try {
      const response = await fetchWithAuth(`${AUTH_API_URL}/api/workspaces`);

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.error || `Failed to list workspaces (${response.status})`);
      }

      const result = await response.json();
      const workspaces: Workspace[] = result.workspaces || [];
      cachedWorkspaces = { data: workspaces, timestamp: Date.now() };
      return workspaces;
    } finally {
      inFlightListWorkspaces = null;
    }
  })();

  return inFlightListWorkspaces;
}

/**
 * Get workspace by ID (deduplicated & short-cached)
 */
export async function getWorkspace(workspaceId: string, forceRefresh = false): Promise<Workspace> {
  const now = Date.now();
  const cached = cachedWorkspaceItems.get(workspaceId);
  if (!forceRefresh && cached && now - cached.timestamp < WORKSPACE_CACHE_TTL_MS) {
    return cached.data;
  }

  const inFlight = inFlightGetWorkspace.get(workspaceId);
  if (inFlight) {
    return inFlight;
  }

  const promise = (async () => {
    try {
      const response = await fetchWithAuth(`${AUTH_API_URL}/api/workspaces/${encodeURIComponent(workspaceId)}`);

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.error || `Failed to get workspace (${response.status})`);
      }

      const result = await response.json();
      const workspace: Workspace = result.workspace;
      cachedWorkspaceItems.set(workspaceId, { data: workspace, timestamp: Date.now() });
      return workspace;
    } finally {
      inFlightGetWorkspace.delete(workspaceId);
    }
  })();

  inFlightGetWorkspace.set(workspaceId, promise);
  return promise;
}

/**
 * Update workspace
 */
export async function updateWorkspace(
  workspaceId: string,
  data: {
    name?: string;
    storage_url?: string;
    profile_type_policy?: string[];
    verification_requirements?: string[];
  }
): Promise<Workspace> {
  invalidateWorkspaceCache();
  const response = await fetchWithAuth(`${AUTH_API_URL}/api/workspaces/${workspaceId}`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to update workspace');
  }

  const result = await response.json();
  invalidateWorkspaceCache();
  return result.workspace;
}

/**
 * Delete workspace
 */
export async function deleteWorkspace(workspaceId: string): Promise<void> {
  invalidateWorkspaceCache();
  const response = await fetchWithAuth(`${AUTH_API_URL}/api/workspaces/${workspaceId}`, {
    method: 'DELETE',
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to delete workspace');
  }
  invalidateWorkspaceCache();
}

/**
 * Add member to workspace
 */
export async function addMember(
  workspaceId: string,
  profileId: string,
  role: string
): Promise<WorkspaceMember> {
  invalidateWorkspaceCache();
  const response = await fetchWithAuth(`${AUTH_API_URL}/api/workspaces/${workspaceId}/members`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      profile_id: profileId,
      role,
    }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to add member');
  }

  const result = await response.json();
  invalidateWorkspaceCache();
  return result.member;
}

/**
 * List workspace members
 */
export async function listMembers(workspaceId: string): Promise<WorkspaceMember[]> {
  const response = await fetchWithAuth(`${AUTH_API_URL}/api/workspaces/${workspaceId}/members`);

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to list members');
  }

  const result = await response.json();
  return result.members;
}

/**
 * Remove member from workspace
 */
export async function removeMember(workspaceId: string, profileId: string): Promise<void> {
  invalidateWorkspaceCache();
  const response = await fetchWithAuth(
    `${AUTH_API_URL}/api/workspaces/${workspaceId}/members/${profileId}`,
    {
      method: 'DELETE',
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to remove member');
  }
  invalidateWorkspaceCache();
}

/**
 * Create or get a DM workspace between current profile and a target profile.
 * Idempotent — same pair always resolves to the same workspace.
 */
export async function createDmWorkspace(
  targetProfileId: string,
  storageUrl?: string
): Promise<{ workspace: Workspace; created: boolean }> {
  invalidateWorkspaceCache();
  const response = await fetchWithAuth(`${AUTH_API_URL}/api/workspaces/dm`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ target_profile_id: targetProfileId, storage_url: storageUrl }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to create DM workspace');
  }

  invalidateWorkspaceCache();
  return response.json();
}

export async function requestSocialContact(profileId: string): Promise<void> {
  const response = await fetchWithAuth(`${AUTH_API_URL}/api/social/contacts/${encodeURIComponent(profileId)}/request`, { method: 'POST' });
  if (!response.ok) throw new Error('Contact request could not be sent');
}

export async function listSocialContactRequests(): Promise<Array<{ profileId: string; name: string }>> {
  const response = await fetchWithAuth(`${AUTH_API_URL}/api/social/contacts/requests`);
  if (!response.ok) throw new Error('Contact requests could not be loaded');
  const data = await response.json();
  return data.requests;
}

export async function respondToSocialContact(profileId: string, accept: boolean): Promise<void> {
  const response = await fetchWithAuth(`${AUTH_API_URL}/api/social/contacts/${encodeURIComponent(profileId)}/respond`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ accept }),
  });
  if (!response.ok) throw new Error('Contact response could not be saved');
}

export async function blockSocialContact(profileId: string): Promise<void> {
  const response = await fetchWithAuth(`${AUTH_API_URL}/api/social/contacts/${encodeURIComponent(profileId)}/block`, { method: 'POST' });
  if (!response.ok) throw new Error('Block could not be saved');
}

export interface SocialGroupInvitation {
  id: string;
  groupId: string;
  groupName: string;
  issuerProfileId: string;
  expiresAt: string;
}

export async function createSocialGroup(name: string): Promise<{ groupId: string; workspaceId: string }> {
  const response = await fetchWithAuth(`${AUTH_API_URL}/api/social/groups`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ name }),
  });
  if (!response.ok) throw new Error((await response.json()).error || 'Group could not be created');
  return (await response.json()).group;
}

export async function inviteToSocialGroup(groupId: string, targetProfileId: string): Promise<{ invitationId: string; workspaceId: string }> {
  const response = await fetchWithAuth(`${AUTH_API_URL}/api/social/groups/${encodeURIComponent(groupId)}/invitations`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ target_profile_id: targetProfileId }),
  });
  if (!response.ok) throw new Error((await response.json()).error || 'Invitation could not be sent');
  return response.json();
}

export async function listSocialGroupInvitations(): Promise<SocialGroupInvitation[]> {
  const response = await fetchWithAuth(`${AUTH_API_URL}/api/social/groups/invitations`);
  if (!response.ok) throw new Error('Group invitations could not be loaded');
  return (await response.json()).invitations;
}

export async function respondToSocialGroupInvitation(id: string, accept: boolean): Promise<{ groupId: string; workspaceId: string; groupName: string; accepted: boolean }> {
  const response = await fetchWithAuth(`${AUTH_API_URL}/api/social/groups/invitations/${encodeURIComponent(id)}/respond`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ accept }),
  });
  if (!response.ok) throw new Error((await response.json()).error || 'Invitation response failed');
  return response.json();
}

export async function leaveSocialGroup(groupId: string): Promise<void> {
  const response = await fetchWithAuth(`${AUTH_API_URL}/api/social/groups/${encodeURIComponent(groupId)}/leave`, { method: 'POST' });
  if (!response.ok) throw new Error((await response.json()).error || 'Could not leave group');
  invalidateWorkspaceCache();
}

export async function submitSocialSafetyReport(input: {
  groupId: string;
  reportedActorProfileId?: string;
  category: string;
  description: string;
  evidence: Array<{ contentType: string; contentId: string; authorProfileId: string; timestamp: string; textPayload?: string }>;
}): Promise<{ id: string; createdAt: string; status: 'submitted' }> {
  const response = await fetchWithAuth(`${AUTH_API_URL}/api/social/safety/reports`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(input),
  });
  if (!response.ok) throw new Error((await response.json()).error || 'Report could not be submitted');
  return (await response.json()).receipt;
}

/**
 * Accept a workspace invitation (invited → active)
 */
export async function acceptWorkspace(workspaceId: string): Promise<WorkspaceMember> {
  invalidateWorkspaceCache();
  const response = await fetchWithAuth(`${AUTH_API_URL}/api/workspaces/${workspaceId}/accept`, {
    method: 'POST',
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to accept workspace invitation');
  }

  const result = await response.json();
  invalidateWorkspaceCache();
  return result.member;
}

/**
 * Decline a workspace invitation
 */
export async function declineWorkspace(workspaceId: string): Promise<void> {
  invalidateWorkspaceCache();
  const response = await fetchWithAuth(`${AUTH_API_URL}/api/workspaces/${workspaceId}/decline`, {
    method: 'POST',
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to decline workspace invitation');
  }
  invalidateWorkspaceCache();
}

/**
 * Leave a workspace (remove yourself as a member).
 * Only non-owner members can use this. Owners must transfer ownership first.
 */
export async function leaveWorkspace(workspaceId: string): Promise<void> {
  invalidateWorkspaceCache();
  const response = await fetchWithAuth(`${AUTH_API_URL}/api/workspaces/${workspaceId}/leave`, {
    method: 'POST',
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to leave workspace');
  }
  invalidateWorkspaceCache();
}

/**
 * Rename a workspace
 */
export async function renameWorkspace(workspaceId: string, name: string): Promise<Workspace> {
  return updateWorkspace(workspaceId, { name });
}

/**
 * Update member role
 */
export async function updateMemberRole(
  workspaceId: string,
  profileId: string,
  role: string
): Promise<WorkspaceMember> {
  invalidateWorkspaceCache();
  const response = await fetchWithAuth(
    `${AUTH_API_URL}/api/workspaces/${workspaceId}/members/${profileId}`,
    {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ role }),
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to update member role');
  }

  const result = await response.json();
  invalidateWorkspaceCache();
  return result.member;
}

// Invitation-related types and functions

export interface Invitation {
  id: string;
  code: string;
  issued_by_account_id: string | null;
  target_email: string | null;
  home_node_id: string | null;
  status: 'unused' | 'used' | 'revoked';
  max_uses: number;
  used_count: number;
  created_at: string;
  expires_at: string | null;
  used_at?: string | null;
  used_by_account_id?: string | null;
}

/**
 * Create a household invitation for a workspace
 */
export async function createHouseholdInvitation(data: {
  workspace_id: string;
  target_email: string;
  expires_at?: string;
}): Promise<Invitation> {
  const response = await fetchWithAuth(`${AUTH_API_URL}/api/invitations/household`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to create household invitation');
  }

  return response.json();
}

/**
 * List pending invitations for a workspace
 */
export async function listWorkspaceInvitations(workspaceId: string): Promise<Invitation[]> {
  const response = await fetchWithAuth(
    `${AUTH_API_URL}/api/invitations/workspace/${workspaceId}`
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to list workspace invitations');
  }

  const result = await response.json();
  return result.invitations;
}

/**
 * Revoke an invitation
 */
export async function revokeInvitation(invitationId: string): Promise<void> {
  const response = await fetchWithAuth(
    `${AUTH_API_URL}/api/invitations/${invitationId}/revoke`,
    {
      method: 'POST',
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to revoke invitation');
  }
}
